
import { Component, Vue, Mixins, Watch } from 'vue-property-decorator'
import Visualization from './Visualization.vue'
import { Options } from '../@types/Slide'
import ChartJS, {
  ChartDataSets,
  ChartOptions,
  ChartLegendLabelItem,
  RadialLinearScale,
  helpers
} from 'chart.js'
import { ChartElement } from '../@types/DatasetController'
import { merge } from 'lodash'

@Component
export default class Chart extends Mixins(Visualization) {
  /**
   * Chart instance.
   */
  public __chart = null

  /**
   * List of basic plugins.
   */
  public baseChartPlugins: any[] = [
    {
      beforeInit(chart) {
        const time = 150
        chart.__timeOuts = []

        chart.data.datasets
          .filter(d => !d.hidden)
          .forEach((dataset, datasetIndex) => {
            dataset.__data = dataset.data
            dataset.data = dataset.__data.map(d => 0)
            chart.__timeOuts.push(
              setTimeout(() => {
                dataset.__data.forEach((data, dataIndex) => {
                  chart.__timeOuts.push(
                    setTimeout(() => {
                      dataset.data[dataIndex] = data
                      chart.update()
                    }, time * dataIndex)
                  )
                })
              }, time * (dataset.__data.length + 3) * datasetIndex)
            )
          })
      },
      beforeUpdate(chart) {
        if (chart.options.sort) {
          const datasets = chart.data.datasets
          const last = datasets[datasets.length - 1]

          const labels = chart.data.labels.sort((a, b) => {
            const i = chart.data.labels.indexOf(a)
            const j = chart.data.labels.indexOf(b)
            return (
              (last.__data[i] - last.__data[j]) * Number(chart.options.sort)
            )
          })

          chart.data.labels = labels

          chart.data.datasets.forEach((dataset, index) => {
            const data = dataset.data.sort((a, b) => {
              const i = dataset.data.indexOf(a)
              const j = dataset.data.indexOf(b)
              return (
                (last.__data[i] - last.__data[j]) * Number(chart.options.sort)
              )
            })

            chart.data.datasets[index].data = data
          })
        }
      }
    }
  ]

  /**
   * Chart type.
   */
  public readonly chartType: string

  /**
   * Global default options.
   */
  public readonly defaultChartOptions: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    responsiveAnimationDuration: 1000,
    plugins: {
      //@ts-ignore
      datalabels: false
    },
    legend: {
      labels: {
        fontSize: 20
      },
      onHover(event: MouseEvent, legendItem) {
        ;(event.target as HTMLElement).style.cursor = 'pointer'
      }
    },
    onHover(event: MouseEvent, chartElement) {
      //@ts-ignore
      this._isHovering = true
      if (chartElement[0]) {
        return ((event.target as HTMLElement).style.cursor = 'pointer')
        //@ts-ignore
      } else if (this.chart.options.links) {
        //@ts-ignore
        const index = this.__vueInstance.findLabeIndexFromEvent(event)
        if (
          index > -1 &&
          //@ts-ignore
          this.options.links.find(({ dataIndex }) => dataIndex === index)
        ) {
          return ((event.target as HTMLElement).style.cursor = 'pointer')
        }
        return ((event.target as HTMLElement).style.cursor = 'default')
      }
      return ((event.target as HTMLElement).style.cursor = 'default')
    },
    onClick(event: MouseEvent, elements: ChartElement[]) {
      if (this.options.links) {
        let link, label
        if (elements.length > 0) {
          //@ts-ignore
          for (let { _datasetIndex, _index } of elements) {
            if (
              (link = this.options.links.find(({ datasetIndex, dataIndex }) => {
                return (
                  (typeof datasetIndex === 'undefined' ||
                    datasetIndex === _datasetIndex) &&
                  (typeof dataIndex === 'undefined' || dataIndex === _index)
                )
              }))
            )
              break
          }
        } else {
          const index = this.__vueInstance.findLabeIndexFromEvent(event)
          link = this.options.links.find(({ dataIndex }) => dataIndex === index)
        }

        if (link) {
          if (link.to) {
            const path =
              link.current === false
                ? link.to
                : { path: `${this.__vueInstance.$route.path}/${link.to}` }
            return this.__vueInstance.$router.push(path)
          } else if (link.path) {
            return window.open(link.path, '_blank')
          }
        }
      }
    }
  }

  /**
   * Find label from position
   */
  public findLabeIndexFromEvent(event: MouseEvent) {
    const {
      pointLabels,
      _pointLabelSizes,
      xCenter,
      yCenter,
      paddingTop,
      drawingArea
    } = this.__chart.scale

    const { x: mouseX, y: mouseY } = helpers.getRelativePosition(
      event,
      this.__chart
    )

    const padding = 10

    return pointLabels.findIndex((label, index) => {
      const size = _pointLabelSizes[index]
      const angle = (((360 / pointLabels.length) * index - 90) * Math.PI) / 180
      const x = Math.round(xCenter + drawingArea * Math.cos(angle))
      const y = Math.round(yCenter + drawingArea * Math.sin(angle))
      const x1 =
        x +
        (x === xCenter
          ? -padding - size.w / 2
          : x > xCenter
          ? -padding / 2
          : -size.w - padding * 1.5)
      const x2 = x1 + size.w + 2 * padding
      const y1 =
        y +
        (y === yCenter
          ? -size.h / 2 - padding / 2
          : y > yCenter
          ? 0
          : -size.h - paddingTop - (x === xCenter ? padding : 0))
      const y2 = y1 + size.h + padding

      return x1 <= mouseX && x2 >= mouseX && y1 <= mouseY && y2 >= mouseY
    })
  }

  /**
   * Find label from position
   */
  public findLabelFromEvent(event: MouseEvent) {
    return this.__chart.scale.pointLabels[this.findLabeIndexFromEvent(event)]
  }

  /**
   * Destroy chart.
   */
  public destroyChart() {
    if (this.__chart) {
      this.__chart.__timeOuts.forEach(clearTimeout)
      this.__chart.destroy()
      this.__chart = null
    }
  }

  /**
   * Render the chart.
   */
  public renderChart() {
    this.__chart = new ChartJS(this.context, {
      type: this.chartType,
      data: this.__data,
      options: this.__options as ChartOptions,
      plugins: this.__plugins
    })

    this.__chart.__vueInstance = this
  }

  /**
   * Destroy the chart on component remove.
   */
  public beforeDestroy() {
    this.destroyChart()
  }

  /**
   * Mounted hook.
   */
  mounted() {
    this.renderChart()
  }
}
